import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EventCard } from "@/components/common/EventCard";
import { EVENT_TYPE_LOUNGE_WIDE } from "@/constants";
import type { EventApiResponse } from "@/lib/humhub-api/types";
import { parseEventDate } from "@/lib/utils/eventUtils";

interface EventListContentProps {
  events: EventApiResponse[];
  date: Date;
  onEventClick: (event: EventApiResponse) => void;
  showOrganizer?: boolean;
}

export const EventListContent: React.FC<EventListContentProps> = ({
  events,
  date,
  onEventClick,
  showOrganizer = false,
}) => {
  const { t, i18n } = useTranslation();

  const formatDate = (date: Date) => {
    return date.toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    });
  };

  // EventApiResponseをEventCardPropsに変換するヘルパー関数
  const convertEventToCardProps = useMemo(() => {
    return (event: EventApiResponse) => {
      try {
        if (!event.start_datetime) {
          console.warn("Missing start_datetime in event:", event);
          return {
            eventCode: event.code || "",
            startDate: "",
            startMonth: "",
            startTime: "",
            startWeekday: "",
            description: event.description,
            is_draft: event.is_draft,
            is_owner: event.is_owner,
            online_location: event.online_location,
            visibility: event.visibility,
            showVisibilityLabel: !showOrganizer,
            title: event.title,
            organizer: showOrganizer
              ? (event.container?.name ?? t("common.notSet"))
              : undefined,
            thumbnailUrl: undefined,
            isImageLoading: false,
            imageError: null,
            onClick: () => onEventClick(event),
            showShareButton: event.visibility === EVENT_TYPE_LOUNGE_WIDE,
            eventId: event.id || 0,
          };
        }

        // 共通のユーティリティ関数を使用して日付をフォーマット
        const dateInfo = parseEventDate(event, i18n.language);

        return {
          eventCode: event.code || "",
          startDate: dateInfo.startDate,
          startMonth: dateInfo.startMonth,
          startTime: dateInfo.startTime,
          startWeekday: dateInfo.startWeekday,
          description: event.description,
          is_draft: event.is_draft,
          is_owner: event.is_owner,
          online_location: event.online_location,
          visibility: event.visibility,
          showVisibilityLabel: !showOrganizer,
          title: event.title,
          organizer: showOrganizer
            ? (event.container?.name ?? t("common.notSet"))
            : undefined,
          thumbnailUrl: undefined,
          isImageLoading: false,
          imageError: null,
          onClick: () => onEventClick(event),
          showShareButton: event.visibility === EVENT_TYPE_LOUNGE_WIDE,
          eventId: event.id,
        };
      } catch (error) {
        console.error(
          "Error converting EventApiResponse to EventCard props:",
          error,
          event,
        );
        return {
          eventCode: event.code || "",
          startDate: "",
          startMonth: "",
          startTime: "",
          startWeekday: "",
          description: event.description,
          is_draft: 0,
          online_location: undefined,
          visibility: event.visibility,
          showVisibilityLabel: !showOrganizer,
          title: event.title || "Error loading event",
          organizer: showOrganizer
            ? (event.container?.name ?? t("common.notSet"))
            : undefined,
          thumbnailUrl: undefined,
          isImageLoading: false,
          imageError: new Error("Failed to convert event data"),
          onClick: () => onEventClick(event),
          showShareButton: false,
          eventId: event.id || 0,
        };
      }
    };
  }, [onEventClick, i18n.language, showOrganizer, t]);

  return (
    <div className="flex size-full flex-col">
      {/* Header - 固定 */}
      <div className="flex shrink-0 items-center justify-between border-b p-6 pb-4">
        <div className="flex-1" />
        <h2 className="text-center text-lg font-semibold">
          {formatDate(date)} {t("eventManage.eventsForDate")}
        </h2>
        <div className="flex-1" />
      </div>

      {/* Content - スクロール可能 */}
      <div
        className="min-h-0 flex-1 overflow-y-auto scrollbar-thin"
        style={{ scrollbarColor: "#d1d5db #f3f4f6" }}
      >
        <div className="p-6 pb-8">
          {events.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <div className="mb-2 text-lg">📅</div>
              <p>{t("eventManage.noEvent")}</p>
            </div>
          ) : (
            <>
              <div className="mb-4 border-b pb-2 text-sm text-gray-600">
                {events.length}
                {t("eventManage.eventNumber")}
              </div>
              <div className="space-y-4">
                {events.map((event) => (
                  <EventCard
                    key={event.id}
                    {...convertEventToCardProps(event)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
